import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, Loader2, X, Cloud, Lock } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'

const POSES = ['front', 'side', 'back']

export function PhotoUploadForm({ onSubmit, uploading }) {
  const { t } = useTranslation()
  const today = new Date().toLocaleDateString('en-CA')
  const [date, setDate] = useState(today)
  const [pose, setPose] = useState('front')
  const [caption, setCaption] = useState('')
  const [file, setFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [storage, setStorage] = useState('cloud')
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setPreviewUrl(URL.createObjectURL(f))
  }

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setFile(null)
    setPreviewUrl(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!file || !date || !pose) return

    const success = await onSubmit({ date, pose, caption, file, storage })
    if (success) {
      clearFile()
      setCaption('')
    }
  }

  const isDisabled = uploading || !file || !date

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Date + Pose row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          dir="ltr"
          className="h-11 px-3 rounded-lg border border-input bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
          required
        />
        <div className="flex gap-2 flex-1">
          {POSES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPose(p)}
              className={cn(
                'flex-1 h-11 rounded-lg text-sm font-medium transition-colors',
                pose === p
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              {t(`phoenixTracker.photos.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {/* File picker / preview */}
      {previewUrl ? (
        <div className="relative">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full max-h-64 object-contain rounded-lg border border-border bg-card"
          />
          <button
            type="button"
            onClick={clearFile}
            className="absolute top-2 end-2 p-2 rounded-full bg-black/60 hover:bg-red-500 text-white transition-colors"
            aria-label="Remove"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-input bg-background hover:bg-accent/50 cursor-pointer transition-colors">
          <Upload className="w-6 h-6 text-muted-foreground mb-2" />
          <span className="text-sm text-muted-foreground">{t('phoenixTracker.photos.selectFile')}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>
      )}

      {/* Caption */}
      <textarea
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder={t('phoenixTracker.photos.captionPlaceholder')}
        rows={2}
        className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-y"
      />

      {/* Storage selector */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">
          {t('phoenixTracker.photos.storage.title')}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setStorage('cloud')}
            className={cn(
              'flex items-center gap-2 p-3 rounded-lg border text-start transition-colors',
              storage === 'cloud'
                ? 'border-app-primary-500 bg-app-primary-50 dark:bg-app-primary-950/30'
                : 'border-input bg-background hover:bg-accent'
            )}
          >
            <Cloud className={cn('w-5 h-5 flex-shrink-0', storage === 'cloud' ? 'text-app-primary-500' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t('phoenixTracker.photos.storage.cloud')}</p>
              <p className="text-[11px] text-muted-foreground truncate">{t('phoenixTracker.photos.storage.cloudDesc')}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setStorage('local')}
            className={cn(
              'flex items-center gap-2 p-3 rounded-lg border text-start transition-colors',
              storage === 'local'
                ? 'border-app-primary-500 bg-app-primary-50 dark:bg-app-primary-950/30'
                : 'border-input bg-background hover:bg-accent'
            )}
          >
            <Lock className={cn('w-5 h-5 flex-shrink-0', storage === 'local' ? 'text-app-primary-500' : 'text-muted-foreground')} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t('phoenixTracker.photos.storage.local')}</p>
              <p className="text-[11px] text-muted-foreground truncate">{t('phoenixTracker.photos.storage.localDesc')}</p>
            </div>
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {storage === 'cloud'
            ? t('phoenixTracker.photos.storage.cloudLong')
            : t('phoenixTracker.photos.storage.localLong')}
        </p>
      </div>

      <Button
        type="submit"
        disabled={isDisabled}
        className="w-full h-11 bg-app-primary-500 hover:bg-app-primary-600 text-white"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('phoenixTracker.photos.uploading')}
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            {t('phoenixTracker.photos.upload')}
          </>
        )}
      </Button>
    </form>
  )
}
